import React, { useEffect, useState, useRef } from 'react';
import { graphql, StaticQuery, Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import '../stylesheets/components/reports.scss';
import { formatDate } from '../utils/format-date';
import iconFilter from '../images/icons/icon-filter.svg';
import DatePicker from "react-datepicker";
import iconCancel from '../images/icons/icon-cancel.svg'
import iconNoDarReports from '../images/icons/icon-no-dar-reports.svg'
import ClientOnly from './internal/clientOnly';
import { useSelector, useDispatch } from "react-redux";
import * as types from "../constants/ActionTypes";

//please update the below css file if you ever update the react-datepicker library
import '../stylesheets/react-datepicker.css'


export default function Reports() {

    const initialCount = 3
    const incrementCount = 2
    const { visibleCount, startDate, endDate, checkedItems, showFiltersMobile } = useSelector(state => state.DarReportsFilters);
    const updateFilters = (data) => {
        dispatch({
            type: types.SET_DAR_REPORTS_FILTERS,
            data
        });
    };
    const dispatch = useDispatch();
    const [minDate, setMinDate] = useState()
    const [maxDate, setMaxDate] = useState()
    const [reduceZindex, setReduceZindex] = useState(false)
    const firstReportRef = useRef(null)
    const fromDateRef = useRef(null)
    const toDateRef = useRef(null)
    const hasSetMinMaxDatesRef = useRef(false);
    const [listHeight, setListHeight] = useState(null);
    const [mounted, setMounted] = useState(false);
    const listRef = useRef()
    const didMount = useRef(false);
    const showClearFilters = Boolean(
        startDate ||
        endDate ||
        (Object.values(checkedItems || {}).some(v => v))
    );  
;

    const nameMap = {
        cap: 'Annual publications',
        at: 'Application trends',
        rb: 'Research briefs',
        sr: 'State reports',
        cs: 'Case studies',
        tpc: 'Research collaboration'
    };

    function renderReports(reports) {
        return reports.slice(0, visibleCount).map((report, index) => (
            <li className={`report-container fade-in`} key={report.node.field_summary}>
                <div className="report-details">
                    <h2 className='category' ref={index === 0? firstReportRef : null}>{nameMap[report.node.field_report_type].replace(/s$/i, '')}</h2>
                    <h3 className='report-title'>
                        <Link to={report.node.path.alias}>{report.node.title} </Link>
                    </h3>
                    <div className="report-publish-details">
                        <span>
                            {report.node.field_report_author_source}
                        </span>
                        <span className='report-date'>
                            {formatDate(report.node.field_date_of_report, report.node.field_date_display_format)}
                        </span>
                    </div>
                    <p>
                        {report.node.field_summary}
                    </p>
                </div>
                <div className="report-image">
                    {report.node.relationships.field_image ?
                        <Link aria-label={report.node.title} to={report.node.path.alias}>
                            <GatsbyImage image={report.node.relationships.field_image.localFile.childImageSharp.gatsbyImageData}
                                className=""
                                alt={report.node.field_image.alt} />
                        </Link> :
                        null
                    }
                </div>
            </li>
        ))
    }

    const handleRemoveFilters = () => {
        updateFilters({
            startDate: null,
            endDate: null,
            checkedItems: {},
            visibleCount: initialCount,
        });
    };
    
    //reset filters when new filter category is chosen
    useEffect (() => {
        if (!didMount.current) {
            didMount.current = true;
            return; // skip reset on first mount
        }

        updateFilters({ visibleCount: initialCount });
    },[startDate, endDate, checkedItems]) 


    //the below two use effects handle the height animation for adding new records
    useEffect(() => {
        if (listRef.current) {
            setTimeout(() => {
                setListHeight(listRef.current.scrollHeight + "px");
                setMounted(true);
            }, 50);
        }
    }, []);

    useEffect(() => {
        if (listRef.current && mounted) {
            setTimeout(() => {
                setListHeight(listRef.current.scrollHeight + "px");
            }, 50)   
        }
    }, [visibleCount, checkedItems, startDate, endDate]);

    //below use effect handles reports list container height when window is resized
    useEffect(() => {
        function updateHeight() {
            if (listRef.current) {
                setListHeight(listRef.current.scrollHeight + "px");
            }
        }

        window.addEventListener("resize", updateHeight);

        return () => {
            window.removeEventListener("resize", updateHeight);
        };
    }, []);

    //handle left  and top margin for clear filters forge button in the sidebar on mobile and desktop
    function useResponsiveMarginLeft() {
        const [marginLeft, setMarginLeft] = React.useState("0px");

        useEffect(() => {
            function updateMargin() {
                const { innerWidth: w, innerHeight: h } = window;

                if (w < 992 ) {
                    // mobile portrait
                    setMarginLeft("16px");
                } else {
                    setMarginLeft("0px");
                }
            }

            updateMargin();
            window.addEventListener("resize", updateMargin);
            return () => window.removeEventListener("resize", updateMargin);
        }, []);
        return marginLeft;
    }

    const marginLeft = useResponsiveMarginLeft();


    function useResponsiveMarginTop() {
        const [marginTop, setMarginTop] = React.useState("0px");

        useEffect(() => {
            function updateMargin() {
                const { innerWidth: w, innerHeight: h } = window;

                if (w < 992 ) {
                    setMarginTop("0px");
                } else {
                    setMarginTop("20px");
                }
            }

            updateMargin();
            window.addEventListener("resize", updateMargin);
            return () => window.removeEventListener("resize", updateMargin);
        }, []);
        return marginTop;
    }

    const marginTop = useResponsiveMarginTop();


    return (
        <>
            <StaticQuery
                query={graphql`query DarReportsAll  {
                    allNodeDarReports(sort: {fields: created, order: DESC}){
                            edges {
                                node {
                                    field_summary
                                    field_link_to_full_report {
                                        uri
                                    },
                                    title,
                                    field_date_of_report,
                                    field_report_author_source,
                                    field_date_display_format,
                                    field_report_type,
                                    field_image {
                                        alt
                                    }
                                    path {
                                        alias
                                    }
                                    relationships {
                                        field_image {
                                            localFile {
                                                childImageSharp {
                                                    gatsbyImageData(width:300 layout: FIXED)
                                                }
                                            }                                            
                                        }
                                    }
                                }
                            }
                        }
                    }`}
                render={data => {

                    let reports = data.allNodeDarReports.edges;
                    
                    //filter out reports with alias starting with /system/
                    reports = reports.filter(report => 
                        !report.node.path.alias.startsWith('/system/')
                    );

                    let tempMinDate = null;
                    let tempMaxDate = null;

                    const filterList = [
                        'Annual publications',
                        'Application trends',
                        'Research briefs',
                        'State reports',
                        'Case studies',
                        'Research collaboration',
                    ];

                    reports.sort((report1, report2) => new Date(report2.node.field_date_of_report) - new Date(report1.node.field_date_of_report))

                    //filtering based on date
                    reports = reports.filter((report) => {
                        const [year, month, day] = report.node.field_date_of_report.split('-').map(Number);

                        const reportDateUTC = new Date(Date.UTC(year, month - 1, day)); 
                        let startDateUTC = startDate
                            ? new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()))
                            : null;

                        let endDateUTC = endDate
                            ? new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth() + 1, 0, 23, 59, 59, 999))
                            : null;

                        if (!tempMinDate || reportDateUTC < tempMinDate) tempMinDate = reportDateUTC
                        if (!tempMaxDate || reportDateUTC > tempMaxDate) tempMaxDate = reportDateUTC

                        if (startDateUTC && endDateUTC) {
                            return reportDateUTC >= startDateUTC && reportDateUTC <= endDateUTC;
                        }

                        if (startDateUTC && !endDateUTC) {
                            return reportDateUTC >= startDateUTC;
                        }

                        if (!startDateUTC && endDateUTC) {
                            return reportDateUTC <= endDateUTC;
                        }
                        return true;
                    })

                    //setting min and max dates
                    if (!hasSetMinMaxDatesRef.current) {
                        if (!minDate || tempMinDate.getTime() !== minDate.getTime()) {
                            const localMin = new Date(
                                tempMinDate.getUTCFullYear(),
                                tempMinDate.getUTCMonth(),
                                tempMinDate.getUTCDate()
                            );
                            setMinDate(localMin);
                        }

                        if (!maxDate || tempMaxDate.getTime() !== maxDate.getTime()) {
                            const localMax = new Date(
                                tempMaxDate.getUTCFullYear(),
                                tempMaxDate.getUTCMonth(),
                                tempMaxDate.getUTCDate()
                            );
                            setMaxDate(localMax);
                        }
                        hasSetMinMaxDatesRef.current = true
                    }

                    const handleCheckboxChange = (event) => {
                        const { name, checked } = event.target;
                        updateFilters({
                            checkedItems: {
                                ...checkedItems,
                                [name]: checked,
                            },
                            visibleCount: initialCount,
                        });
                    };

                    //filtering based on report type
                    reports = reports.filter((report) => {
                        if (Object.values(checkedItems).some(value => value === true)) {
                            return Object.keys(checkedItems).some((item) => {
                                return checkedItems[item] && nameMap[report.node.field_report_type] === item;
                            });
                        } else {
                            return true;
                        }
                    });


                    return (
                        <>
                            <div id='report-container' className="reports container">
                                <div className="row">
                                    <div className='col-lg-4 filters-container'>
                                        <button className="show-filter-button" aria-expanded={showFiltersMobile} aria-controls="filtersPanel" onClick={() =>  updateFilters({ showFiltersMobile: !showFiltersMobile })}>
                                            <img src={iconFilter} alt="" />
                                            <span style={{ color: 'black'}}>Filters</span>
                                            {showFiltersMobile ? <span className='plus-minus-sign'>&#65293;</span>
                                                : <span className='plus-minus-sign'>&#65291;</span>}
                                        </button>
                                        <div
                                            style={{
                                                display: "inline-block",
                                                minHeight: "44px",   
                                                marginTop,
                                                verticalAlign: "middle"
                                            }}
                                        >
                                            {showClearFilters ? (
                                                <ClientOnly>
                                                    <forge-button
                                                        onClick={handleRemoveFilters}
                                                        color="secondary"
                                                        fill="outline"
                                                        size="m"
                                                        style={{
                                                            opacity: showClearFilters ? 1 : 0,
                                                            marginLeft
                                                        }}
                                                    >
                                                        Clear filters
                                                        <img src={iconCancel} alt="" />
                                                    </forge-button>
                                                </ClientOnly>
                                            ) : (
                                                <span style={{ display: "inline-block", height: "44px", width: "1px", verticalAlign: "middle" }} />
                                            )}
                                        </div>

                                        <div
                                            className={`filter-wrapper ${showFiltersMobile ? "filter-open" : ""
                                                }`}
                                            aria-hidden={!showFiltersMobile} id="filtersPanel"
                                        >
                                            <div className='report-type-filter-container'>
                                                <hr className="filter-divider"></hr>
                                                <fieldset >
                                                    <legend className='filter-title'>Type</legend>
                                                    {filterList.map((reportType) => {
                                                        const id = `filter-${reportType.replace(/\s+/g, '-').toLowerCase()}`;
                                                        return (
                                                            <div key={id} className="report-type-filter">
                                                                <label className="report-type-filter-label" htmlFor={id}>
                                                                    <input
                                                                        type="checkbox"
                                                                        id={id}
                                                                        name={reportType}
                                                                        checked={!!checkedItems[reportType]}
                                                                        onChange={handleCheckboxChange}
                                                                    />
                                                                    {reportType}
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </fieldset>
                                            </div>
                                            <div>
                                                <hr className="filter-divider"></hr>
                                                <fieldset>
                                                    <legend className='filter-title'>Date Range</legend>
                                                    <label htmlFor="from" className='label-from'>From</label>
                                                    <DatePicker id="from" selected={startDate} dateFormat="MMMM yyyy"
                                                        showMonthYearPicker
                                                        minDate={minDate}
                                                        maxDate={endDate? endDate : maxDate}
                                                        onChange={(date) => updateFilters({ startDate: date })}
                                                        isClearable
                                                        customInputRef={fromDateRef}
                                                        onCalendarOpen={() => setReduceZindex(true)}
                                                        onCalendarClose={() => {
                                                            fromDateRef.current?.blur()
                                                            setReduceZindex(false)
                                                        }} />
                                                    <br />
                                                    <label htmlFor="to" className='label-to'>To</label>
                                                    <DatePicker id="to" selected={endDate} dateFormat="MMMM yyyy"
                                                        showMonthYearPicker
                                                        minDate={startDate ? startDate : minDate}
                                                        maxDate={maxDate}
                                                        onChange={(date) => updateFilters({ endDate: date })}
                                                        isClearable
                                                        customInputRef={toDateRef}
                                                        onCalendarOpen={() => setReduceZindex(true)}
                                                        onCalendarClose={() => {
                                                            toDateRef.current?.blur()
                                                            setReduceZindex(false)
                                                        }} />
                                                </fieldset>
                                            </div>
                                        </div>
                                    </div>
                                    <main className={"col-lg-8 main-container" + (reduceZindex ? " reduce-z-index" : " ")} >
                                        <ul className='reports-list' ref={listRef} style={{
                                            maxHeight: listHeight,
                                            transition: mounted ? "max-height 0.5s ease" : "none"
                                        }} >
                                            {(reports.length === 0) ? (<div className="no-dar-reports-message">
                                                <img src={iconNoDarReports} alt="" />
                                                <span>No reports match your criteria.</span>
                                                <span>Try removing one or more filters to see results.</span>
                                                <div>
                                                    <ClientOnly>
                                                        <forge-button
                                                            color="primary"
                                                            fill="solid"
                                                            size="l"
                                                            //className="no-reports-clear-filter"
                                                            onClick={handleRemoveFilters}>
                                                            Clear filters
                                                        </forge-button>
                                                    </ClientOnly>
                                                </div>
                                            </div>) : (
                                                    <React.Fragment >
                                                        {renderReports(reports)}
                                                    </React.Fragment>
                                            )}
                                        </ul>
                                        {reports.length !== 0 ? <div style={{marginBottom: "40px"}}>
                                            <ClientOnly>
                                                <forge-button
                                                    style={{ display: visibleCount < reports.length ? 'inline-block' : 'none', marginRight: '10px', marginBottom: '20px', verticalAlign: 'middle' }}
                                                    color="primary"
                                                    fill="solid"
                                                    size="m"
                                                    onClick={() => {
                                                        updateFilters({ visibleCount: visibleCount + incrementCount })
                                                    }}
                                                ><forge-icon library="regular" name="plus" size="l" viewbox="0 0 512 512">Plus</forge-icon>
                                                    View more reports
                                                </forge-button>
                                                <forge-button
                                                    style={{ display: (visibleCount >= reports.length && reports.length > initialCount) ? 'inline-block' : 'none', marginBottom: '20px', verticalAlign: 'middle' }}
                                                    color="primary"
                                                    fill="solid"
                                                    size="m"
                                                    onClick={() => {
                                                        firstReportRef.current?.scrollIntoView({ block: 'center' });
                                                        setTimeout(() => {
                                                            updateFilters({ visibleCount: initialCount });
                                                        }, 2200);
                                                    }}
                                                >View less</forge-button>
                                            </ClientOnly>
                                            <ClientOnly>
                                                <forge-button
                                                    style={{ display: visibleCount < reports.length ? 'inline-block' : 'none', marginBottom: '20px', verticalAlign: 'middle' }}
                                                    className="view-all-button"
                                                    color="primary"
                                                    fill="outline"
                                                    size="m"
                                                    onClick={() => {
                                                        updateFilters({ visibleCount: reports.length });
                                                    }}
                                                >    <span>
                                                        View all ({reports.length})
                                                    </span>
                                                </forge-button>
                                            </ClientOnly>
                                        </div> : null
                                        }
                                    </main>
                                </div>
                            </div>
                        </>
                    )
                }
                }
            />
        </>)
}
